import React, { useState } from 'react';

import { MoreVert } from '@mui/icons-material';
import { IconButton, Menu, MenuItem, alpha, useTheme } from '@mui/material';

export interface ActionButton {
	id: string;
	icon: React.ReactNode;
	title: string;
	onClick: (e: React.MouseEvent) => void;
	color?: string;
	backgroundColor?: string;
	isActive?: boolean;
	activeColor?: string;
	activeBackgroundColor?: string;
}

export interface MenuAction {
	id: string;
	icon: React.ReactNode;
	label: string;
	onClick: (e: React.MouseEvent) => void;
	color?: string;
}

interface ActionButtonsMenuProps {
	/** Array of buttons to show directly in the toolbar */
	mainButtons: ActionButton[];
	/** Array of actions to show in the "More actions" menu */
	menuActions: MenuAction[];
	/** Whether to show the more actions button */
	showMoreActions?: boolean;
	/** Custom styling for the container */
	containerStyle?: React.CSSProperties;
	/** Custom styling for individual buttons */
	buttonStyle?: React.CSSProperties;
}

export default function ActionButtonsMenu({ mainButtons, menuActions, showMoreActions = true, containerStyle, buttonStyle }: ActionButtonsMenuProps) {
	const theme = useTheme();
	const defaultButtonStyle: React.CSSProperties = {
		backgroundColor: theme.palette.background.default,
		color: theme.palette.text.secondary,
		width: '24px',
		height: '24px',
		fontSize: '12px',
	};
	const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
	const menuOpen = Boolean(menuAnchor);

	const handleMenuOpen = (e: React.MouseEvent<HTMLElement>) => {
		e.stopPropagation();
		setMenuAnchor(e.currentTarget);
	};

	const handleMenuClose = () => {
		setMenuAnchor(null);
	};

	const handleMenuItemClick = (action: MenuAction) => (e: React.MouseEvent) => {
		e.stopPropagation();
		handleMenuClose();
		action.onClick(e);
	};

	const getButtonStyle = (button: ActionButton) => {
		const baseStyle = { ...defaultButtonStyle, ...buttonStyle };

		if (button.isActive) {
			return {
				...baseStyle,
				backgroundColor: button.activeBackgroundColor || button.backgroundColor || baseStyle.backgroundColor,
				color: button.activeColor || button.color || theme.palette.text.secondary,
			};
		}

		return {
			...baseStyle,
			backgroundColor: button.backgroundColor || baseStyle.backgroundColor,
			color: button.color || theme.palette.text.secondary,
		};
	};

	return (
		<>
			<div
				style={{
					position: 'absolute',
					top: '4px',
					right: '4px',
					display: 'flex',
					gap: '2px',
					...containerStyle,
				}}
			>
				{/* Render main buttons */}
				{mainButtons.map((button) => (
					<IconButton key={button.id} size="small" onClick={button.onClick} style={getButtonStyle(button)} title={button.title}>
						{button.icon}
					</IconButton>
				))}

				{/* More actions button */}
				{showMoreActions && menuActions.length > 0 && (
					<IconButton
						size="small"
						onClick={handleMenuOpen}
						style={{
							...defaultButtonStyle,
							...buttonStyle,
							color: theme.palette.text.secondary,
						}}
						title="More actions"
					>
						<MoreVert style={{ fontSize: '14px' }} />
					</IconButton>
				)}
			</div>

			{/* Action Menu */}
			{showMoreActions && menuActions.length > 0 && (
				<Menu
					anchorEl={menuAnchor}
					open={menuOpen}
					onClose={handleMenuClose}
					onClick={(e) => e.stopPropagation()}
					PaperProps={{ sx: { boxShadow: `0 2px 8px ${alpha(theme.palette.common.black, 0.15)}` } }}
				>
					{menuActions.map((action) => (
						<MenuItem key={action.id} onClick={handleMenuItemClick(action)} style={{ fontSize: '14px', minHeight: '36px' }}>
							<span style={{ fontSize: '16px', marginRight: '8px', color: action.color || theme.palette.text.secondary }}>{action.icon}</span>
							{action.label}
						</MenuItem>
					))}
				</Menu>
			)}
		</>
	);
}
